import React from 'react';
import styled from 'styled-components';
import {COLORS, FONTS, IconBadge, Image, Line, SIZES, Typography} from '@fupa/fupa-uikit';
import {formatDayMonthDate} from 'app/helpers/dateHelpers';
import MatchResult from 'app/components/match/matchRow/MatchResult';
import {getCompetitorKey, getMatchMediaFlags, getMatchRowColor} from 'app/components/match/matchRow/matchRowHelpers';
import {
  ArrowDown,
  ArrowUp,
  Assist,
  Card,
  Emblem,
  Football,
  TopEleven,
  Tracktics,
  TwoCards,
  PenaltyMissed,
} from 'app/components/icons';
import {IconButton} from '@mui/material';
import {KeyboardArrowRight, Timer} from '@mui/icons-material';
import {useHistory} from 'react-router';
import {LinkOnComponent} from 'app/components/links/link';

const Row = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatsLine = styled(Line)`
  &&& {
    margin: 0 1rem;
  }
`;

const CustomIconBadge = styled(IconBadge)`
  &&& {
    background-color: transparent;
    box-shadow: none;
    border: none;
    top: -0.625rem;
    right: -0.625rem;
  }
`;

const MatchResultSC = styled.div`
  margin-top: 0.25rem;
  position: relative;
`;

const Wrapper = styled.div`
  display: flex;
  height: 4rem;
  width: 100%;
`;

const MatchInfo = styled.div`
  display: flex;
  height: inherit;
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  padding-left: 1rem;
  margin-right: 0.25rem;
  align-items: center;
`;

const MatchTile = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const EmblemContainer = styled.div`
  margin: 0 1rem;
  width: ${SIZES[32]};
  height: ${SIZES[32]};
`;

const MatchEmblems = styled.div`
  margin: 0 1rem;
  gap: 4px;
  display: flex;
  flex-direction: row;
`;

const VerticalLine = styled.span`
  width: 1px;
  height: 2.5rem;
  background-color: ${COLORS.lighterGrey};
`;

const MatchResultSmall = styled(MatchResult)`
  &&& {
    display: flex;
    height: ${SIZES['16']};
    max-height: ${SIZES['16']};
    & span {
      font-size: ${FONTS['12']};
    }
  }
`;

const MatchStats = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
`;

const Stat = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  border-radius: 1.5rem;
  height: 1.5rem;
  padding: 0 0.375rem;
  background-color: ${COLORS.lightestGrey};
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  margin-left: 0.5rem;
`;

const Count = styled(Typography)`
  &&& {
    margin-right: 0.125rem;
  }
`;

const Action = styled.div`
  align-self: center;
  margin-right: 0.25rem;
`;

const iconStyle = {fontSize: 16, margin: 'auto'};
const statConfig = {
  // Player-Stats
  minutes: {icon: <Timer style={{...iconStyle, color: COLORS.darkGrey}} />},
  assists: {icon: <Assist style={{...iconStyle, color: COLORS.darkGrey}} />},
  topEleven: {icon: <TopEleven style={iconStyle} />, withoutCount: true},
  details: {icon: <Tracktics style={iconStyle} />, withoutCount: true},
  goals: {icon: <Football style={{...iconStyle, color: COLORS.darkGrey}} />},
  penaltyMisses: {icon: <PenaltyMissed style={{...iconStyle}} />},
  penaltyHits: {icon: <Football style={{...iconStyle, color: COLORS.green}} />},
  redCard: {icon: <Card style={{...iconStyle, color: COLORS.red}} />, withoutCount: true},
  substituteIn: {icon: <ArrowUp style={{...iconStyle, color: COLORS.greenIn}} />},
  substituteOut: {icon: <ArrowDown style={{...iconStyle, color: COLORS.red}} />},
  yellowCard: {icon: <Card style={{...iconStyle, color: COLORS.yellowCard}} />, withoutCount: true},
  yellowRedCard: {icon: <TwoCards style={iconStyle} />, withoutCount: true},
  // Referee-Stats
  redCards: {icon: <Card style={{...iconStyle, color: COLORS.red}} />},
  yellowCards: {icon: <Card style={{...iconStyle, color: COLORS.yellowCard}} />},
  yellowRedCards: {icon: <TwoCards style={iconStyle} />},
};

const ProfilePlayerMatchStats = ({ownTeamSlug, match, type}) => {
  const {statistics, flags, kickoff} = match;
  const history = useHistory();
  const competitorKey = getCompetitorKey(ownTeamSlug, match);
  const competitor = match[competitorKey];

  const goToMatch = () => {
    history.push(`/match/${match.slug}`);
  };

  const competitorEmblem = competitor?.image ? (
    <Image entity='emblem' src={competitor.image} width={32} height={32} fit={true} />
  ) : (
    <Emblem style={{color: COLORS.lightGrey, fontSize: 32}} />
  );
  let matchRowColor = type === 'player' ? getMatchRowColor(match, null, ownTeamSlug) : COLORS.lighterGrey;

  // 'minutes' need to be the first stat that should be rendered
  // Need to remove key 'minutes' from array first to change index to 0
  const statKeys = Object.keys(statistics).filter(element => element !== 'minutes');
  if (type === 'player') {
    statKeys?.unshift('minutes');
  }
  const stats = statKeys?.map(stat => {
    const filterAvailable = stat === 'details' ? statistics[stat]?.length > 0 : statistics[stat] > 0;
    if (filterAvailable && Boolean(statConfig[stat])) {
      let countValue = statistics[stat];
      countValue += stat === 'minutes' ? `'` : '';
      const count = statConfig[stat].withoutCount ? null : <Count variant='subheading'>{countValue}</Count>;
      return (
        <Stat key={stat}>
          {count}
          {statConfig[stat].icon}
        </Stat>
      );
    }
  });

  const fallbackEmblem = <Emblem style={{color: COLORS.lightGrey, fontSize: 20}} />;

  const emblems =
    type === 'player' ? (
      <EmblemContainer>{competitorEmblem}</EmblemContainer>
    ) : (
      <MatchEmblems>
        {match.homeTeam?.image ? (
          <Image entity='emblem' src={match.homeTeam.image} width={20} height={20} fit={true} />
        ) : (
          fallbackEmblem
        )}
        {match.awayTeam?.image ? (
          <Image entity='emblem' src={match.awayTeam.image} width={20} height={20} fit={true} />
        ) : (
          fallbackEmblem
        )}
      </MatchEmblems>
    );

  const icon = getMatchMediaFlags(flags, 12);

  return (
    <Row>
      <Wrapper>
        <MatchInfo as={LinkOnComponent} to={`/match/${match.slug}`} enablehover={true}>
          <Typography variant='caption1'>{formatDayMonthDate(kickoff)}</Typography>
          <MatchTile>
            {emblems}
            <MatchResultSC>
              <MatchResultSmall homeGoal={match.homeGoal} awayGoal={match.awayGoal} color={matchRowColor} />
              <CustomIconBadge value={icon} position={2} />
            </MatchResultSC>
          </MatchTile>
          <VerticalLine />
        </MatchInfo>
        <MatchStats>{stats}</MatchStats>
        <Action>
          <IconButton onClick={goToMatch} size='large'>
            <KeyboardArrowRight color={'action'} />
          </IconButton>
        </Action>
      </Wrapper>
      <StatsLine />
    </Row>
  );
};

export {ProfilePlayerMatchStats};
